import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Shield } from 'lucide-react';
import { PageLoadingSkeleton } from '../common/LoadingSkeleton';
import { sanitizePath, isSafeInternalPath } from '../../utils/routingUtils';
import { isAllowedRedirectPath } from '../../constants/routing';

interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  redirectTo?: string;
}

/**
 * GuestGuard component that only allows unauthenticated users to access auth pages
 * Redirects authenticated users away from login, register, forgot-password, etc.
 */
export const GuestGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <PageLoadingSkeleton />
    );
  }

  if (currentUser) {
    // User is authenticated, redirect them away from auth pages
    // BUT: If email is not verified, redirect to verification page instead
    const isEmailVerified = currentUser.emailVerified || false;
    
    if (!isEmailVerified) {
      // Unverified user - redirect to verification page
      return <Navigate to="/email-verification" replace />;
    }
    
    // Verified user - redirect to intended destination or home
    // SECURITY: Validate location.state pathname to prevent open redirect attacks
    const raw = location.state?.from?.pathname || '/';
    const candidate = sanitizePath(raw, 500);
    
    // SECURITY: Validate path is safe and in whitelist
    const isSafe = isSafeInternalPath(candidate) && isAllowedRedirectPath(candidate);
    const redirectTo = isSafe ? candidate : '/';
    
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

/**
 * AuthGuard component that protects routes requiring authentication
 * Shows authentication required UI for non-authenticated users
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({ 
  children, 
  fallback,
  redirectTo = '/login'
}) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <PageLoadingSkeleton />
    );
  }

  if (!currentUser) {
    // User is not authenticated
    if (fallback) {
      return <>{fallback}</>;
    }

    // SECURITY: Store the intended destination for post-login redirect
    // Validate path is safe and in whitelist before storing
    const raw = `${location.pathname}${location.search || ''}`;
    const candidate = sanitizePath(raw, 500);
    
    // SECURITY: Validate path is safe and in whitelist
    const isSafe = isSafeInternalPath(candidate) && isAllowedRedirectPath(candidate);
    const returnUrl = isSafe ? candidate : '/';
    
    // SECURITY: Only store return URL if it's not an auth page
    if (returnUrl !== '/login' && returnUrl !== '/register' && returnUrl !== '/forgot-password') {
      sessionStorage.setItem('authReturnUrl', returnUrl);
    }

    // SECURITY: Validate redirectTo prop is safe
    const sanitizedRedirectTo = sanitizePath(redirectTo, 500);
    const isRedirectSafe = isSafeInternalPath(sanitizedRedirectTo) && 
                          (sanitizedRedirectTo === '/login' || sanitizedRedirectTo === '/register');
    const safeRedirect = isRedirectSafe ? sanitizedRedirectTo : '/login';
    
    return <Navigate to={safeRedirect} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

/**
 * VerifiedGuard component that ensures users have verified their email
 * Redirects unverified users to email verification page
 * 
 * IMPORTANT: Uses currentUser.emailVerified directly from Firebase Auth
 * to prevent flickering caused by state updates. Firebase Auth persists
 * the verification status, so we don't need to check on every render.
 * 
 * Only checks verification status once per route, not on every render.
 */
export const VerifiedGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  const redirectHandledRef = React.useRef(false);

  // PERFORMANCE: Reset redirect flag only when pathname changes
  // This hook must be called at the top level, before any early returns
  React.useEffect(() => {
    redirectHandledRef.current = false;
  }, [location.pathname]);

  if (loading) {
    return <PageLoadingSkeleton />;
  }

  // If no user, let AuthGuard handle it
  if (!currentUser) {
    return <>{children}</>;
  }

  // Use Firebase Auth's persisted emailVerified status directly
  // This prevents flickering from state updates
  const isEmailVerified = currentUser.emailVerified || false;

  // If user exists but email not verified, redirect to verification page
  // Allow access to verification page itself and auth action handler
  if (!isEmailVerified && 
      location.pathname !== '/email-verification' && 
      !location.pathname.includes('/auth-action')) {
    // Only redirect once per route change
    if (!redirectHandledRef.current) {
      redirectHandledRef.current = true;
      return <Navigate to="/email-verification" replace />;
    }
    return <PageLoadingSkeleton />;
  }

  // If user is already verified and on verification page, redirect to home
  // This prevents verified users from seeing the verification page
  if (isEmailVerified && location.pathname === '/email-verification') {
    // Only redirect once per route change
    if (!redirectHandledRef.current) {
      redirectHandledRef.current = true;
      return <Navigate to="/" replace />;
    }
    return <PageLoadingSkeleton />;
  }

  return <>{children}</>;
};

/**
 * AuthenticationRequired component - Reusable UI for auth-required states
 */
export const AuthenticationRequired: React.FC<{
  title?: string;
  message?: string;
  showRegisterButton?: boolean;
  onLogin?: () => void;
  onRegister?: () => void;
}> = ({ 
  title = "Authentication Required",
  message = "Please log in to access this feature",
  showRegisterButton = true,
  onLogin,
  onRegister
}) => {
  const navigate = useNavigate();

  const handleLogin = () => {
    if (onLogin) {
      onLogin();
    } else {
      navigate('/login');
    }
  };

  const handleRegister = () => {
    if (onRegister) {
      onRegister();
    } else {
      navigate('/register');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-4">
      <div className="text-center max-w-md mx-auto">
        <div className="w-20 h-20 bg-gradient-to-br from-[#da2516] to-red-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-xl">
          <Shield className="w-10 h-10 text-white" />
        </div>
        <h3 className="text-2xl font-poppins font-bold text-gray-900 mb-4">{title}</h3>
        <p className="text-gray-600 mb-8 font-poppins leading-relaxed">{message}</p>
        <div className="space-y-4">
          <button
            onClick={handleLogin}
            className="w-full px-6 py-4 bg-gradient-to-r from-[#da2516] to-red-600 text-white rounded-xl hover:from-red-600 hover:to-[#da2516] transition-all duration-200 font-poppins font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            Go to Login
          </button>
          {showRegisterButton && (
            <button
              onClick={handleRegister}
              className="w-full px-6 py-4 bg-white text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-poppins font-semibold border border-gray-200 shadow-sm hover:shadow-md"
            >
              Create Account
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestGuard;
